import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiCheckCircle, FiLoader } from 'react-icons/fi';
import { exchangeEmailProviderCode } from '../profile/emailIntegrations';
import { useLanguage } from '../contexts/LanguageContext';

function providerFromState(state) {
  const lower = (state || '').toLowerCase();
  if (lower.includes('microsoft') || lower.includes('outlook')) return 'outlook';
  return 'gmail';
}

function OutreachEmailCallback({ user, onProfileSaved }) {
  const { language, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const processedRef = useRef(false);
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState(language === 'ar' ? 'جارٍ ربط حساب بريدك الإلكتروني…' : 'Connecting your email account...');

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const handleCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');
      const provider = providerFromState(state);

      if (error) {
        setStatus('error');
        setMessage(language === 'ar' ? 'فشل تفويض حساب البريد الإلكتروني.' : `Authorization failed: ${error}`);
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage(language === 'ar' ? 'لم يصل رمز التفويض.' : 'No authorization code was received.');
        return;
      }

      try {
        const result = await exchangeEmailProviderCode({ user, provider, code, state });
        if (onProfileSaved) {
          await onProfileSaved({
            'emailAutomation/connected': true,
            'emailAutomation/provider': provider,
            'emailAutomation/email': result.email || '',
            'emailAutomation/updatedAt': Date.now(),
          });
        }
        setStatus('success');
        setMessage(language === 'ar' ? `تم ربط ${result.email || 'حساب بريدك الإلكتروني'} بنجاح.` : `Successfully connected ${result.email || 'your email account'}.`);
        window.setTimeout(() => {
          const redirectPath = result.redirectPath || '/settings';
          const separator = redirectPath.includes('?') ? '&' : '?';
          navigate(`${redirectPath}${separator}email_connected=1&email_provider=${encodeURIComponent(provider)}`, { replace: true });
        }, 1400);
      } catch (callbackError) {
        console.error('Email OAuth callback failed:', callbackError);
        setStatus('error');
        setMessage(language === 'ar' ? 'تعذّر ربط حساب البريد الإلكتروني.' : callbackError.message || 'Unable to connect this email account.');
      }
    };

    handleCallback();
  }, [language, location.search, navigate, onProfileSaved, user]);

  const Icon = status === 'success' ? FiCheckCircle : status === 'error' ? FiAlertTriangle : FiLoader;

  return (
    <section className={`js-oauth-callback is-${status}`} aria-labelledby="oauth-callback-title">
      <div className="js-oauth-callback__card">
        <Icon aria-hidden="true" />
        <h1 id="oauth-callback-title">
          {status === 'success'
            ? t('settings.email.connected')
            : status === 'error'
              ? t('settings.email.connectionFailed')
              : t('settings.email.checking')}
        </h1>
        <p>{message}</p>
        {status === 'error' && (
          <button type="button" onClick={() => navigate('/settings', { replace: true })}>
            {language === 'ar' ? 'العودة إلى الإعدادات' : 'Back to Settings'}
          </button>
        )}
      </div>
    </section>
  );
}

export default OutreachEmailCallback;
